<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { usePolicy } from 'dashboard/composables/usePolicy';

import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import LabelActions from 'dashboard/components/widgets/conversation/conversationBulkActions/LabelActions.vue';
import ContactListActions from './ContactListActions.vue';

const props = defineProps({
  visibleContactIds: {
    type: Array,
    default: () => [],
  },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  activeListId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits([
  'clearSelection',
  'assignLabels',
  'assignLists',
  'removeFromList',
  'moveToLists',
  'toggleAll',
  'deleteSelected',
]);

const { t } = useI18n();
const { checkPermissions } = usePolicy();

const selectedCount = computed(() => props.selectedContactIds.length);
const totalVisibleContacts = computed(() => props.visibleContactIds.length);
const showMenu = ref(false);
const showLabelSelector = ref(false);
const showListSelector = ref(false);
const listSelectorMode = ref('add');

const selectAllLabel = computed(() => {
  if (!totalVisibleContacts.value) {
    return '';
  }

  return t('CONTACTS_BULK_ACTIONS.SELECT_ALL', {
    count: totalVisibleContacts.value,
  });
});

const selectedCountLabel = computed(() =>
  t('CONTACTS_BULK_ACTIONS.SELECTED_COUNT', {
    count: selectedCount.value,
  })
);

const allItems = computed(() =>
  props.visibleContactIds.map(id => ({
    id,
  }))
);

const selectionModel = computed({
  get: () => new Set(props.selectedContactIds),
  set: newSet => {
    if (!props.visibleContactIds.length) {
      emit('toggleAll', false);
      return;
    }

    const shouldSelectAll =
      newSet.size === props.visibleContactIds.length && newSet.size > 0;
    emit('toggleAll', shouldSelectAll);
  },
});

const menuItems = computed(() => {
  const items = [
    {
      label: t('CONTACTS_BULK_ACTIONS.LISTS.ADD'),
      action: 'add',
      value: 'add',
      icon: 'i-lucide-list-plus',
    },
    {
      label: t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS'),
      action: 'labels',
      value: 'labels',
      icon: 'i-lucide-tags',
    },
  ];

  if (props.activeListId) {
    items.push(
      {
        label: t('CONTACTS_BULK_ACTIONS.LISTS.REMOVE'),
        action: 'remove',
        value: 'remove',
        icon: 'i-lucide-list-minus',
      },
      {
        label: t('CONTACTS_BULK_ACTIONS.LISTS.MOVE'),
        action: 'move',
        value: 'move',
        icon: 'i-lucide-arrow-right-left',
      },
      {
        label: t('CONTACTS_BULK_ACTIONS.LISTS.REMOVE_ALL'),
        action: 'removeAll',
        value: 'removeAll',
        icon: 'i-lucide-list-x',
      }
    );
  }

  if (checkPermissions(['administrator'])) {
    items.push({
      label: t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS'),
      action: 'delete',
      value: 'delete',
      icon: 'i-lucide-trash',
    });
  }

  return items;
});

const closePanels = () => {
  showMenu.value = false;
  showLabelSelector.value = false;
  showListSelector.value = false;
};

const emitClearSelection = () => {
  closePanels();
  emit('clearSelection');
};

const openListSelector = mode => {
  if (!selectedCount.value || props.isLoading) return;
  listSelectorMode.value = mode;
  showListSelector.value = true;
  showLabelSelector.value = false;
  showMenu.value = false;
};

const handleAssignLabels = labels => {
  emit('assignLabels', labels);
  closePanels();
};

const handleListSubmit = listIds => {
  if (listSelectorMode.value === 'move') emit('moveToLists', listIds);
  else emit('assignLists', listIds);
  closePanels();
};

const handleAction = ({ action }) => {
  if (action === 'add') {
    openListSelector('add');
    return;
  }
  if (action === 'labels') {
    showMenu.value = false;
    showListSelector.value = false;
    showLabelSelector.value = true;
    return;
  }
  if (action === 'remove') {
    closePanels();
    emit('removeFromList', { allLists: false });
    return;
  }
  if (action === 'move') {
    openListSelector('move');
    return;
  }
  if (action === 'removeAll') {
    closePanels();
    emit('removeFromList', { allLists: true });
    return;
  }
  if (action === 'delete') {
    closePanels();
    emit('deleteSelected');
  }
};
</script>

<template>
  <div
    class="sticky top-0 z-[1] bg-gradient-to-b from-n-surface-1 from-90% to-transparent pt-1 pb-2"
  >
    <BulkSelectBar
      v-model="selectionModel"
      :all-items="allItems"
      :select-all-label="selectAllLabel"
      :selected-count-label="selectedCountLabel"
      class="py-2 ltr:!pr-3 rtl:!pl-3 justify-between"
    >
      <template #secondary-actions>
        <Button
          sm
          ghost
          slate
          :label="t('CONTACTS_BULK_ACTIONS.CLEAR_SELECTION')"
          class="!px-1.5"
          @click="emitClearSelection"
        />
      </template>
      <template #actions>
        <div
          v-on-click-outside="closePanels"
          class="relative flex items-center ml-auto"
        >
          <Button
            sm
            faded
            slate
            icon="i-lucide-chevron-down"
            :label="t('CONTACTS_BULK_ACTIONS.MENU')"
            :disabled="!selectedCount || isLoading"
            :is-loading="isLoading"
            @click="showMenu = !showMenu"
          />
          <DropdownMenu
            v-if="showMenu"
            :menu-items="menuItems"
            class="ltr:right-0 rtl:left-0 mt-1 w-56 top-full"
            @action="handleAction"
          />
          <LabelActions
            v-if="showLabelSelector"
            class="[&>.triangle]:!hidden [&>div>button]:!hidden ltr:!right-0 rtl:!left-0 top-8 mt-0.5"
            @assign="handleAssignLabels"
          />
          <ContactListActions
            v-if="showListSelector"
            :mode="listSelectorMode"
            @submit="handleListSubmit"
          />
        </div>
      </template>
    </BulkSelectBar>
  </div>
</template>
